import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { MapPin, ArrowRight } from "@phosphor-icons/react";
import { POSTCARD_IMG, LOGO_IMG } from "@/lib/brand";

const HERO = POSTCARD_IMG;

export default function ConsumerLanding() {
  const [campaigns, setCampaigns] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", zip_code: "", campaign_id: "" });
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/campaigns?status=published").then(({ data }) => {
      setCampaigns(data);
      if (data.length) setForm((f) => ({ ...f, campaign_id: data[0].id }));
    });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.campaign_id) { toast.error("Please select your area"); return; }
    setLoading(true);
    try {
      await api.post("/subscribers", form);
      toast.success("You're in! Unlocking your local deals…");
      nav(`/deals/${form.campaign_id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center">
      <div className="absolute inset-0">
        <img src={HERO} alt="9x12 PMI postcard mailer" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy-950/95 via-navy-900/85 to-navy-900/40" />
        <div className="absolute inset-0 grain opacity-40" />
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 py-20 grid lg:grid-cols-2 gap-16 items-center">
        <div className="text-white">
          <img src={LOGO_IMG} alt="Pinpoint Media Intelligence" className="h-20 w-auto bg-white rounded-lg p-2.5 mb-6 shadow-xl" />
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            Unlock Exclusive<br />Local Deals
          </h1>
          <p className="mt-6 text-lg text-slate-200 max-w-md leading-relaxed font-[300]">
            Your neighborhood's best offers, delivered on one beautifully curated 9×12 postcard. Enter your details to reveal hand-picked deals near you.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="backdrop-blur-xl bg-white/90 border border-white/40 shadow-2xl p-8 md:p-12 lg:ml-auto lg:max-w-md w-full"
          data-testid="optin-card"
        >
          <img src={LOGO_IMG} alt="PMI" className="h-10 w-auto mb-4" />
          <h2 className="font-display text-2xl sm:text-3xl tracking-tight text-navy-900">Reveal My Deals</h2>
          <p className="text-sm text-slate-500 mt-2 mb-8">Free. No spam. Local savings only.</p>
          <form onSubmit={submit} className="space-y-4">
            <input
              required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Full name" data-testid="optin-name"
              className="w-full border border-slate-300 px-4 py-3 focus:ring-2 focus:ring-navy-900 focus:outline-none bg-white"
            />
            <input
              required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="Email address" data-testid="optin-email"
              className="w-full border border-slate-300 px-4 py-3 focus:ring-2 focus:ring-navy-900 focus:outline-none bg-white"
            />
            <div className="relative">
              <MapPin size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                required value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })}
                placeholder="Zip code" data-testid="optin-zip"
                className="w-full border border-slate-300 pl-11 pr-4 py-3 focus:ring-2 focus:ring-navy-900 focus:outline-none bg-white"
              />
            </div>
            <select
              value={form.campaign_id} onChange={(e) => setForm({ ...form, campaign_id: e.target.value })}
              data-testid="optin-campaign"
              className="w-full border border-slate-300 px-4 py-3 focus:ring-2 focus:ring-navy-900 focus:outline-none bg-white"
            >
              {campaigns.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
            <button
              type="submit" disabled={loading} data-testid="optin-submit"
              className="w-full bg-navy-900 text-white py-4 font-[600] hover:bg-emerald-700 hover:-translate-y-0.5 transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-60 disabled:hover:translate-y-0 disabled:cursor-not-allowed"
            >
              {loading ? "Unlocking…" : <>Unlock Deals <ArrowRight size={18} /></>}
            </button>
          </form>
          <div className="mt-6 pt-5 border-t border-slate-200 flex items-center justify-center gap-4 text-xs text-slate-500" data-testid="modal-legal-footer">
            <Link to="/privacy" className="hover:text-navy-900 underline-offset-2 hover:underline" data-testid="modal-privacy-link">Privacy Policy</Link>
            <span className="text-slate-300">•</span>
            <Link to="/terms" className="hover:text-navy-900 underline-offset-2 hover:underline" data-testid="modal-terms-link">Terms of Service</Link>
          </div>
        </motion.div>
      </div>

      <footer className="absolute bottom-0 inset-x-0 z-10 px-6 md:px-12 py-5 flex flex-col sm:flex-row items-center justify-center sm:justify-between gap-2 text-xs text-slate-300" data-testid="landing-footer">
        <span>© {new Date().getFullYear()} Pinpoint Media Intelligence. All rights reserved.</span>
        <div className="flex items-center gap-4">
          <Link to="/privacy" className="hover:text-white underline-offset-2 hover:underline" data-testid="footer-privacy-link">Privacy Policy</Link>
          <span className="text-slate-500">•</span>
          <Link to="/terms" className="hover:text-white underline-offset-2 hover:underline" data-testid="footer-terms-link">Terms of Service</Link>
        </div>
      </footer>
    </div>
  );
}
